// <NewHiresCard> — Home View Step 4 (T-043, plan §3 / spec §6).
//
// Vertical list of recent hires (last 30 days, server-filtered + sorted).
// Initial 3 rows visible; "Show more" expands to all. Each row links
// the hire's name to their profile.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { HomeNewHire } from '@pa/shared';
import { Avatar } from './Avatar';

export interface NewHiresCardProps {
  hires: HomeNewHire[];
}

const INITIAL_ROWS = 3;

export function NewHiresCard({ hires }: NewHiresCardProps) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? hires : hires.slice(0, INITIAL_ROWS);
  const hiddenCount = Math.max(0, hires.length - INITIAL_ROWS);

  return (
    <section
      data-testid="home-new-hires"
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
    >
      <header className="p-4 border-b border-slate-100 flex justify-between items-center">
        <h3 className="text-sm font-bold text-slate-800">New hires</h3>
      </header>
      {hires.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-400 italic">
          No new hires this month
        </div>
      ) : (
        <>
          <ul className="divide-y divide-slate-50">
            {visible.map((h) => (
              <li
                key={h.id}
                className="p-4 flex justify-between items-center"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar
                    name={h.name}
                    avatarUrl={h.avatarUrl}
                    sizeClass="w-10 h-10"
                    className="border border-slate-100"
                  />
                  <div className="min-w-0">
                    <Link
                      to={`/employees/${h.id}`}
                      className="block text-sm font-bold text-slate-800 hover:text-[#6344C6] text-left truncate"
                      data-testid="home-new-hires-name"
                    >
                      {h.name}
                    </Link>
                    {h.title && (
                      <p className="text-xs text-slate-400 truncate">{h.title}</p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 pl-3">
                  <p className="text-sm font-bold text-slate-800">
                    {formatDayMonth(h.startDate)}
                  </p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                    {formatRelative(h.daysSinceStart)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 transition-colors border-t border-slate-100"
              data-testid="home-new-hires-show-more"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </>
      )}
    </section>
  );
}

// `YYYY-MM-DD` → `DD-MM`. Server always sends ISO so we don't need
// timezone-aware parsing here.
function formatDayMonth(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${m[3]}-${m[2]}`;
}

function formatRelative(daysSinceStart: number): string {
  if (daysSinceStart <= 0) return 'TODAY';
  if (daysSinceStart === 1) return '1 DAY AGO';
  if (daysSinceStart < 30) return `${daysSinceStart} DAYS AGO`;
  // 30+ days falls outside the server's NEW_HIRE_WINDOW_DAYS (30) so
  // we shouldn't see this in practice; print a sane fallback anyway.
  if (daysSinceStart < 365) {
    const months = Math.round(daysSinceStart / 30);
    return `${months} MONTH${months === 1 ? '' : 'S'} AGO`;
  }
  const years = Math.round(daysSinceStart / 365);
  return `${years} YEAR${years === 1 ? '' : 'S'} AGO`;
}
